import { acceptHMRUpdate, defineStore } from "pinia";

/**
 * An API call
 */
interface ApiCall<T = any> {
  fetch: Promise<T>;
}

/**
 * Our store's state
 */
interface Data {
  /**
   * The currently in progress API calls
   */
  inProgress: Record<string, ApiCall>;
}

/**
 * A store for de-duplicating identical concurrent API calls
 */
export const useApiStore = defineStore( "api", {
  state: (): Data => ( {
    inProgress: {},
  } ),
  actions: {
    /**
     * Capture the given API call and de-dupe it using the given key
     * @param key The API call key
     * @param call The API call
     */
    captureApiCall<T = any> ( key: string, call: ()=> Promise<T> ) {
      // If we don't currently have a call with this key in progress...
      if ( !this.isInProgress( key ) ) {
        // Make the call
        const res = call()
          .then( ( data ) => {
            return data;
          } )
          .finally( () => {
            // Always clean up our in progress calls when they finish, even if it errors
            delete this.inProgress[key];
          } );

        // Track the call in our state
        this.inProgress[key] = {
          fetch: res,
        };

        // Return the call promise
        return res;
      } else {
        // We have a call under this key already, just return it
        return this.inProgress[key].fetch as Promise<T>;
      }
    },
    /**
     * Check whether the key is in our store
     * @param key
     */
    isInProgress ( key: string ) {
      return !!this.inProgress[key];
    },
  },
} );

/**
 * Enable HMR with Vite
 */
if ( import.meta.hot ) {
  import.meta.hot.accept( acceptHMRUpdate( useApiStore, import.meta.hot ) );
}
